import React from "react";
import {
  BookOpen,
  Check,
  CheckCircle2,
  MessageSquare,
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { API } from "../../lib/api";
import { WORKSPACE } from "../../constants/testIds";

function absAssetUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || API.replace(/\/api$/, "")).replace(/\/$/, "");
  return `${base}${url}`;
}

function fmtPrice(currency, amount) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return `${currency || "USD"} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function QuotationCard({
  offer,
  busy,
  blocked,
  onView,
  onSelect,
  onClarify,
}) {
  const supplier = offer.supplier || {};
  const name = supplier.name || offer.supplier_id;
  const logoSrc = absAssetUrl(offer.supplier_logo_url || supplier.logo_url);
  const catalogHref = absAssetUrl(offer.supplier_catalog_url || supplier.catalog_url);
  const isSelected = offer.status === "Selected";
  const isPublished = offer.status === "Published";

  return (
    <article
      className={`rounded-xl border bg-white p-5 transition-shadow hover:shadow-sm ${
        isSelected ? "border-emerald-300 ring-1 ring-emerald-200" : "border-neutral-200"
      }`}
      data-testid={WORKSPACE.quotationCard}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-16 w-32 flex items-center justify-center overflow-hidden">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt=""
              className="h-full w-full object-contain object-center"
            />
          ) : (
            <span className="text-2xl font-semibold text-neutral-400 uppercase" aria-hidden>
              {(name || "?").trim().charAt(0)}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0 pl-3 sm:pl-5">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-[hsl(var(--brand-navy))] leading-snug">{name}</h3>
            {isSelected && (
              <Badge className="rounded-sm bg-emerald-700 text-white text-[10px] uppercase">Selected</Badge>
            )}
          </div>
          <p className="text-sm sm:text-base text-emerald-600 font-medium mt-1 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            Verified Manufacturer
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {onClarify && (
              <button
                type="button"
                onClick={() => onClarify(offer.id)}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-[hsl(var(--brand-navy))] hover:bg-neutral-50"
              >
                <MessageSquare className="h-4 w-4 shrink-0" />
                Contact Manufacturer
              </button>
            )}
            {catalogHref && (
              <a
                href={catalogHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-[hsl(var(--brand-navy))]/15 bg-[hsl(var(--brand-navy))]/5 px-3 py-1.5 text-xs font-semibold text-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy))]/10"
                data-testid={WORKSPACE.viewCatalogBtn}
              >
                <BookOpen className="h-4 w-4 shrink-0" />
                View catalog
              </a>
            )}
          </div>
        </div>

        <div className="shrink-0 min-w-[168px] rounded-xl border border-[hsl(var(--brand-navy))]/20 bg-[hsl(var(--brand-navy))]/5 px-4 py-3 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-500">Unit Price</p>
          <p className="text-2xl font-bold tabular-nums text-[hsl(var(--brand-navy))] mt-1.5 leading-none">
            {fmtPrice(offer.currency, offer.unit_price)}
          </p>
          <p className="text-xs font-medium text-neutral-600 mt-1.5">/ {offer.packaging || "unit"}</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-neutral-100 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
        <Stat label="Lead Time" value={offer.lead_time || "—"} highlight />
        <Stat label="MOQ" value={offer.moq || "—"} />
        <Stat label="Incoterm" value={offer.incoterm || "—"} />
        <Stat label="Sample" value={offer.sample_available ? "Available" : "—"} success={offer.sample_available} />
        <Stat label="Valid Until" value={offer.offer_validity || "—"} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2 justify-end">
        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => onView(offer.id)}>
          View details
        </Button>
        {isPublished && !blocked && (
          <Button
            size="sm"
            className="rounded-sm h-8 bg-[hsl(var(--brand-navy))] text-white"
            disabled={busy}
            onClick={() => onSelect(offer.id)}
            data-testid={WORKSPACE.selectOfferBtn}
          >
            <Check className="w-3.5 h-3.5 mr-1" /> Select
          </Button>
        )}
      </div>
    </article>
  );
}

function Stat({ label, value, highlight, success }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-400 font-semibold">{label}</div>
      <div
        className={`mt-1 font-medium flex items-center gap-1.5 ${
          success ? "text-emerald-600" : "text-neutral-800"
        }`}
      >
        {highlight && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />}
        {value}
      </div>
    </div>
  );
}
